import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Dialog } from "@/components/ui/Dialog";
import { Button } from "@/components/ui/Button";
import { ConfirmDialog } from "@/components/dialogs/ConfirmDialog";
import { SubmitForm } from "@/components/submit/SubmitForm";
import { IdeaThankYou } from "@/components/submit/IdeaThankYou";
import { api } from "@/lib/api";
import { useAuth } from "@/hooks/useAuth";
import { showToast } from "@/lib/toast";
import { t } from "@/lib/strings";
import type { UUID } from "@/lib/types";

/**
 * The ticket-submission form rendered as a modal on top of the current screen
 * (blurred/darkened backdrop via the Dialog primitive) — the inbox-style way to
 * raise a request without leaving Home. Reuses the same SubmitForm as the
 * standalone /submit/$boardId page. The /submit route stays for shareable links.
 */
export function SubmitTicketDialog({
  open,
  onOpenChange,
  boardId,
  boardName,
  workspaceName,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  boardId: UUID | null;
  boardName?: string;
  workspaceName?: string;
}) {
  const { user } = useAuth();
  const [dirty, setDirty] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  // Ideenbox celebration: after a successful idea submission we keep the dialog
  // open and swap the body to a thank-you + confetti instead of toast-and-close.
  // `formNonce` remounts a fresh SubmitForm for "Noch eine Idee".
  const [submitted, setSubmitted] = useState(false);
  const [formNonce, setFormNonce] = useState(0);

  // Each fresh open starts clean (the dialog body is only mounted while open).
  useEffect(() => {
    if (open) {
      setSubmitted(false);
      setDirty(false);
    }
  }, [open]);

  const infoQuery = useQuery({
    queryKey: ["board-submit-info", boardId],
    queryFn: () => api.getSubmitInfo(boardId as UUID),
    enabled: open && !!boardId,
    retry: false,
  });

  const title = boardName ?? infoQuery.data?.name ?? t.submitTitle;
  const description = workspaceName ? `${t.submitTitle} · ${workspaceName}` : t.submitDescription;

  // Radix calls onOpenChange(false) on backdrop-click + Esc. If the requester
  // has already typed something, keep the dialog open and ask for confirmation
  // (styled, not window.confirm) before discarding the work. A pristine form
  // closes instantly — an accidental open you never touched shouldn't nag.
  // Mirrors NewTicketDialog; both wrap the same SubmitForm.
  function requestClose(next: boolean) {
    if (!next && dirty) {
      setConfirmOpen(true);
      return;
    }
    setDirty(false);
    onOpenChange(next);
  }

  function discardAndClose() {
    setConfirmOpen(false);
    setDirty(false);
    onOpenChange(false);
  }

  return (
    <>
      <Dialog
        open={open}
        onOpenChange={requestClose}
        title={title}
        description={description}
        width={560}
      >
        {infoQuery.isLoading ? (
          <div style={{ padding: "8px 2px", fontSize: 13, color: "var(--ink-3)" }}>{t.loading}</div>
        ) : infoQuery.error || !infoQuery.data ? (
          <div style={{ padding: "8px 2px", fontSize: 13, color: "var(--danger)" }}>
            {t.submitDisabledBody}
          </div>
        ) : submitted ? (
          <IdeaThankYou>
            <Button
              variant="secondary"
              size="md"
              icon="plus"
              onClick={() => {
                setSubmitted(false);
                setFormNonce((n) => n + 1);
              }}
            >
              {t.ideaThanksAnother}
            </Button>
            <Button variant="primary" size="md" icon="check" onClick={() => onOpenChange(false)}>
              {t.ideaThanksClose}
            </Button>
          </IdeaThankYou>
        ) : (
          <SubmitForm
            key={formNonce}
            boardId={infoQuery.data.id}
            detail={infoQuery.data}
            currentUserSub={user?.sub ?? null}
            onDirtyChange={setDirty}
            onSubmitted={() => {
              setDirty(false);
              // Ideenbox → celebrate in place; other boards keep toast-and-close.
              if (infoQuery.data?.kind === "ideas") {
                setSubmitted(true);
              } else {
                showToast(t.submitDankeBody, "success");
                onOpenChange(false);
              }
            }}
          />
        )}
      </Dialog>
      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title={t.discardTitle}
        message={t.discardMessage}
        confirmLabel={t.discardConfirm}
        cancelLabel={t.discardCancel}
        tone="danger"
        onConfirm={discardAndClose}
      />
    </>
  );
}
